import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import { get } from "lodash";

import Collapse from "./Collapse";
import { getOptionLabel } from "common/enums";
import { applicationTypes } from "common/constant";

const SewageDemandView = ({
  applicationTypeId,
  sewageDemand,
  isExpandable = true,
  t,
}) => {
  const {
    waterDemand,
    waterDemandUnit,
    increaseOfWaterDemand,
    decreaseOfWaterDemand,
    increaceOrDecreaseOfWaterDemandUnit,
    totalWaterDemand,
    totalWaterDemandUnit,
    flushing,
    flushingUnit,
    domestic,
    domesticUnit,
    gardening,
    gardeningUnit,
    totalFlushing,
    totalDomestic,
    totalGardening,
    totalFlushingUnit,
    totalDomesticUnit,
    totalGardeningUnit,
    increaseOfFlushing,
    increaseOfDomestic,
    increaseOfGardening,
    decreaseOfFlushing,
    decreaseOfDomestic,
    decreaseOfGardening,
    increaceOrDecreaseOfFlushingUnit,
    increaceOrDecreaseOfDomesticUnit,
    increaceOrDecreaseOfGardeningUnit,
  } = sewageDemand;

  const isDemandExtensionApplication = useMemo(
    () => applicationTypes["Demand Extension"] === applicationTypeId,
    [applicationTypeId, applicationTypes]
  );

  const isDemandReductionApplication = useMemo(
    () => applicationTypes["Demand Reduction"] === applicationTypeId,
    [applicationTypeId, applicationTypes]
  );

  const fields = useMemo(
    () => [
      {
        name: "Existing Water Demand",
        key: "waterDemand",
        renderField: () =>
          waterDemand
            ? `${waterDemand} ${getOptionLabel("unitOptions", waterDemandUnit)}`
            : "N/A",

        isRequired: true,
        isVisible: isDemandExtensionApplication || isDemandReductionApplication,
      },

      {
        name: "Increase of Water Demand",
        key: "increaseOfWaterDemand",
        isRequired: true,
        renderField: () =>
          `${increaseOfWaterDemand} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfWaterDemandUnit
          )}`,
        isVisible: isDemandExtensionApplication,
      },
      {
        name: "Decrease of Water Demand",
        key: "decreaseOfWaterDemand",
        isRequired: true,
        renderField: () =>
          `${decreaseOfWaterDemand} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfWaterDemandUnit
          )}`,
        isVisible: isDemandReductionApplication,
      },
      {
        name: "Total Water Demand",
        key: "totalWaterDemand",
        isRequired: true,
        renderField: () =>
          totalWaterDemand
            ? `${totalWaterDemand} ${getOptionLabel(
                "unitOptions",
                totalWaterDemandUnit
              )}`
            : "N/A",
      },
      {
        name: "Existing Flushing",
        key: "flushing",
        renderField: () =>
          flushing
            ? `${flushing} ${getOptionLabel("unitOptions", flushingUnit)}`
            : "N/A",

        isRequired: true,
        isVisible: isDemandExtensionApplication || isDemandReductionApplication,
      },

      {
        name: "Increase of Flushing",
        key: "increaseOfFlushing",
        isRequired: true,
        renderField: () =>
          `${increaseOfFlushing} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfFlushingUnit
          )}`,
        isVisible: isDemandExtensionApplication,
      },
      {
        name: "Decrease of Flushing",
        key: "decreaseOfFlushing",
        isRequired: true,
        renderField: () =>
          `${decreaseOfFlushing} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfFlushingUnit
          )}`,
        isVisible: isDemandReductionApplication,
      },
      {
        name: "Total Flushing",
        key: "totalFlushing",
        renderField: () =>
          totalFlushing
            ? `${totalFlushing} ${getOptionLabel(
                "unitOptions",
                totalFlushingUnit
              )}`
            : "N/A",
        isRequired: true,
      },
      {
        name: "Existing Domestic",
        key: "domestic",
        renderField: () =>
          domestic
            ? `${domestic} ${getOptionLabel("unitOptions", domesticUnit)}`
            : "N/A",
        isRequired: true,
        isVisible: isDemandExtensionApplication || isDemandReductionApplication,
      },
      {
        name: "Increase of Domestic",
        key: "increaseOfDomestic",
        isRequired: true,
        renderField: () =>
          `${increaseOfDomestic} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfDomesticUnit
          )}`,
        isVisible: isDemandExtensionApplication,
      },
      {
        name: "Decrease of Domestic",
        key: "decreaseOfDomestic",
        isRequired: true,
        renderField: () =>
          `${decreaseOfDomestic} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfDomesticUnit
          )}`,
        isVisible: isDemandReductionApplication,
      },
      {
        name: "Total Domestic",
        key: "totalDomestic",
        renderField: () =>
          totalDomestic
            ? `${totalDomestic} ${getOptionLabel(
                "unitOptions",
                totalDomesticUnit
              )}`
            : "N/A",
        isRequired: true,
      },
      {
        name: "Existing Gardening",
        key: "gardening",
        renderField: () =>
          gardening
            ? `${gardening} ${getOptionLabel("unitOptions", gardeningUnit)}`
            : "N/A",
        isRequired: true,
        isVisible: isDemandExtensionApplication || isDemandReductionApplication,
      },

      {
        name: "Increase of Gardening",
        key: "increaseOfGardening",
        isRequired: true,
        renderField: () =>
          `${increaseOfGardening} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfGardeningUnit
          )}`,
        isVisible: isDemandExtensionApplication,
      },
      {
        name: "Decrease of Gardening",
        key: "decreaseOfGardening",
        isRequired: true,
        renderField: () =>
          `${decreaseOfGardening} ${getOptionLabel(
            "unitOptions",
            increaceOrDecreaseOfGardeningUnit
          )}`,
        isVisible: isDemandReductionApplication,
      },
      {
        name: "Total Gardening",
        key: "totalGardening",
        renderField: () =>
          totalGardening
            ? `${totalGardening} ${getOptionLabel(
                "unitOptions",
                totalGardeningUnit
              )}`
            : "N/A",
        isRequired: true,
      },
    ],
    [sewageDemand]
  );
  return (
    <Collapse title="Sewage Demand Details" isExpandable={isExpandable}>
      <Row className="d-flex mb-2">
        {fields.map(({ isVisible, name, key, isRequired, renderField }) => {
          const value = get(sewageDemand, key, "");
          if (typeof isVisible !== "boolean" || isVisible) {
            return (
              <Col key={key} lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t(name)}:</Col>
                  <Col
                    md={7}
                    className="fw-semibold"
                    style={{ wordBreak: "break-word" }}
                  >
                    {renderField
                      ? renderField(value)
                      : get(sewageDemand, key, null) ??
                        (isRequired ? "-" : "N/A")}
                  </Col>
                </Row>
              </Col>
            );
          }
        })}
      </Row>
    </Collapse>
  );
};

SewageDemandView.propTypes = {
  applicationTypeId: PropTypes.number,
  isExpandable: PropTypes.bool,
  sewageDemand: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(SewageDemandView);
